import moment from 'moment';
import React from 'react';
import { investmentClassesType, NumberFieldType } from 'src/types';

export const appendPropsToChildren = (children: React.ReactNode, props: Object) => {
  return React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, props);
  });
};

//Returns the numeric value of a given input.
//If the object is not a string the original object will be returned
export function getNumberFromString(value: any) {
  return typeof value === 'string' ? Number(value.replace(/[^0-9\.-]+/g, '')) : value;
}

/**
 * @description adding the line breaks in the description
 */
export function createMarkup(text) {
  // A regular expression to match content outside of HTML tags
  const regex = /(^|<\/?[^>]+>)([^<>]+)/g;

  // Function to replace line breaks with <br> tags outside of HTML tags
  const replaceLineBreaksOutsideOfTags = (match, tag, text) => {
    // Only replace line breaks in the text outside of HTML tags
    return `${tag}${text.replace(/(?:\r\n|\r|\n)/g, '<br>')}`;
  };

  // Replace line breaks outside of HTML tags with <br> tags
  const htmlText = text?.replace(regex, replaceLineBreaksOutsideOfTags);

  return htmlText;
}

/**
 * Adding commas to the value or return null to show place holder if none was provided
 * @param value Value to display with commas
 * @param currency Optional currency type in string format ('USD')
 * @param numOfDecimals Number of decimals to show
 * @param allowNegative Whether to allow negative values
 * @returns String number with commas
 */
export const displayNumbersWithCommas = (
  value: number,
  currency: string = null,
  numOfDecimals = 0,
  allowNegative = false,
) => {
  //return null if value is not valid
  if (!value) return null;

  if (value <= 0 && !allowNegative) return null;

  //return us format - add currency if provided
  return currency
    ? value.toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: numOfDecimals,
      })
    : value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: numOfDecimals,
      });
};

/**
 * Adding commas to the value or return null to show place holder if none was provided
 * @param value Value to display with commas
 * @param decimals Number of decimals to show
 * @returns String number with commas
 */
export const displayPercentages = (value: number, decimals?: number): string => {
  if (!value || value <= 0) return null;
  // round the value to the specified number of decimals
  const valueWithDecimals = decimals ? value.toFixed(decimals) : value;

  const strippedValue = valueWithDecimals.toString().replace('%', '').replace('.00', ''); //remove the % sign and the decimal if it is 00

  return strippedValue + '%';
};

/**
 * Returns the date only in a text format
 * @param date Given date
 * @param returnNull Whether to return null or empty string
 * @returns Date only format
 */
export const getDateString = (date: Date, returnNull: boolean = false): string => {
  //no date
  if (!date) return returnNull ? null : '';

  //set months const
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  //get the iso string and remove time
  const formattedDate = getUtcDate(date).toISOString().split('T')[0].split('-');

  //create a DD-MMM-YYYY format
  formattedDate[1] = months[parseInt(formattedDate[1]) - 1];
  formattedDate.reverse();
  return formattedDate.join('-');
};

/**
 * Returns the date in mongo api format
 * @param date Given date
 * @returns Date api format
 */
export const getDateApiFormat = (date: Date): string => {
  //no date
  if (!date) return '';

  return moment(date).format('YYYY-MM-DD');
};

/**
 * Compares the date component of the given dates are equal
 * @param date1 First date
 * @param date2 Second date
 * @returns Returns whether the date component of the given dates are equal
 */
export const areDatesEqual = (date1: Date, date2: Date): boolean => {
  //remove time component
  const date1NoTime = new Date(date1)?.setHours(0, 0, 0, 0);
  const date2NoTime = new Date(date2)?.setHours(0, 0, 0, 0);

  //check that both dates are not null and return their equality
  if (!date1NoTime) return false;
  return date1NoTime === date2NoTime;
};

/**
 * Convert a given en-GB string format to date
 * @param dateStr Date in a en-GB format
 * @returns en-US date object
 */
export const convertGBStringToDate = (dateStr: string): Date => {
  if (!dateStr) return null;

  //split date and validate results
  const splitDate = dateStr.split('-');
  if (splitDate?.length !== 3) return null;

  var months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  //split year month and day
  // const month = Number(splitDate[1]) - 1; //Javascript months are 0-11
  return new Date(Number(splitDate[2]), months[splitDate[1].toLowerCase()], Number(splitDate[0]));
};

/**
 * Clone an object using stringify
 * @param obj Object to be cloned
 * @returns Cloned object
 */
export function deepClone(obj: any) {
  return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

/**
 * Returns whether the given array is null or empty
 * @param array Given array
 * @returns Whether the given array is null or empty
 */
export function isArrayEmpty(array: any[]) {
  return !array || array?.length === 0;
}

/**
 * Convert the date to utc format while keeping the exact same date
 * @param localDate Given local date
 * @returns Date in UTC format
 */
export function getUtcDate(localDate: Date) {
  var date = new Date(localDate);
  var utcDate = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );
  return utcDate;
}

/**
 * Returns the months difference between two dates.
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns The months difference between the two dates.
 */
export function getMonthsDifference(date1: Date, date2: Date): number {
  if (!date1 || !date2) return null; //validate input
  let months = (date2.getFullYear() - date1.getFullYear()) * 12;
  months -= date1.getMonth() + 1;
  months += date2.getMonth() + 1;
  return months <= 0 ? 0 : months;
}

/**
 * Gets whether the trimmed lower case text value are equal
 * @param text1 Given text 1
 * @param text2 Given text 2
 * @param notEmpty Whether to check both texts are not empty
 * @returns Whether the trimmed lower case text value are equal
 */
export const isBaseStringsEqual = (
  text1: string,
  text2: string,
  notEmpty: boolean = false,
): boolean => {
  if (text1?.trim().toLowerCase() === '' && notEmpty) return false;
  return text1?.trim().toLowerCase() === text2?.trim().toLowerCase();
};

/**
 * Return the string value of the date as the first day of the month
 * @param dateInput Given Date
 * @returns String value of the date as the first day of the month
 */
export const getFirstDayDateString = (dateInput: Date): string => {
  if (!dateInput) return null;

  //firstDay
  const firstDay = new Date(new Date(dateInput).getFullYear(), new Date(dateInput).getMonth(), 1);
  return getDateString(firstDay);
};

/** Returns a new array of objects with unique values based on a specific property of the objects.
 * @param objects - The array of objects to filter.
 * @param uniqueBy - The property name to use to create a unique array.
 * @returns A new array of objects with unique values based on the specified property.
 */
export function uniqueArrayOfObjects(objects: any[], uniqueBy: string): any[] {
  const uniqueValues = new Set(); //create a set of unique values
  const uniqueArray = []; //init the unique id

  //loop over each object
  objects.forEach(obj => {
    //if object value is not in the unique added array add it to the return array and the unique array
    if (!uniqueValues.has(obj[uniqueBy])) {
      uniqueValues.add(obj[uniqueBy]);
      uniqueArray.push(obj);
    }
  });

  return uniqueArray;
}

/** Returns a new array of objects with unique values based on a specific property of the objects.
 * @param objects - The array of objects to filter.
 * @param uniqueBy - a list of property name to use to create a unique array.
 * @returns A new array of objects with unique values based on the specified property.
 */
export function uniqueArrayOfManyObjects(objects: any[], uniqueBy: string[]): any[] {
  const uniqueValues = new Set(); //create a set of unique values
  const uniqueArray = []; //init the unique id

  //loop over each object
  objects.forEach(obj => {
    let isUnique = true;
    //if object value is not in the unique added array add it to the return array and the unique array
    for (let i = 0; i < uniqueBy.length; i++) {
      if (uniqueValues.has(obj[uniqueBy[i]])) {
        isUnique = false;
        break;
      }
    }

    //if this is a unique object add it to the array and known values
    if (isUnique) {
      for (let i = 0; i < uniqueBy.length; i++) {
        uniqueValues.add(obj[uniqueBy[i]]);
      }
      uniqueArray.push(obj);
    }
  });

  return uniqueArray;
}

/**
 * Adds comma separating thousand on a given string that represents a number
 * @param input Given string representing number
 * @returns The string with thousand separated with comma
 */
export const addThousandsCommaToString = (input: string): string => {
  const noCommaText = input.replaceAll(',', ''); //remove comma from org string
  //take the part after the . and add thousand comma
  const parts = noCommaText.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

/**
 * Construct NumberFieldType from a given string representing a number
 * This is useful for textField forms which require to show a get a number input
 * @param input Given string representing number
 * @returns The NumberFieldType object
 */
export const constructNumberFieldType = (input: string, suffix: string = null): NumberFieldType => {
  //validate input
  if (!input || input === '') return { numValue: 0, textValue: '' };

  //remove comma and suffix from org string
  const noSuffix = suffix ? input.replace(suffix, '') : input;
  const noCommaText = noSuffix.replaceAll(',', '');

  const numValue = parseFloat(noCommaText); //get the number value

  //take the part after the . and add thousand comma
  const parts = noCommaText.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const suffixAddition = suffix ?? '';
  const textValue = parts.join('.') + suffixAddition;

  //return the constructed value
  const returnedObj: NumberFieldType = { numValue, textValue };
  return returnedObj;
};

/**
 * Extracts the number from a given text
 * @param text Given text
 * @returns Number extracted from the text
 */
export const extractNumberFromText = (text: string): number => {
  if (!text) return null;
  return parseFloat(text.replace(/[^0-9.]/g, ''));
};

/**
 * Normalize a value to a Date object at midnight UTC (00:00:00).
 * This function ensures that the input is a valid Date object before normalizing.
 * @param dateInput The value to normalize (can be a Date object, string, or undefined).
 * @returns A Date object normalized to midnight UTC, or null if the input is invalid.
 */
const normalizeDate = (dateInput: any): Date | null => {
  let date: Date;

  // If input is a valid Date object
  if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
    date = dateInput;
  }
  // If input is a string or number, try converting to Date
  else if (typeof dateInput === 'string' || typeof dateInput === 'number') {
    date = new Date(dateInput);
    if (isNaN(date.getTime())) return null; // Invalid date from string/number
  } else {
    return null; // Invalid input, return null
  }

  // Normalize to midnight UTC (00:00:00 UTC)
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

/**
 * Gets the days difference between two dates, with an option to add months to the start date.
 * It disregards hours, minutes, seconds, and milliseconds by normalizing both dates to the start of the day (midnight UTC).
 * @param date1 The earlier date (start date)
 * @param date2 The later date (end date)
 * @param monthsPeriod Optional months to add to the start date before calculating the difference
 * @returns The days difference between the two dates, adjusted for leap years if applicable.
 */
export const getDaysDiff = (
  date1: Date,
  date2: Date,
  monthsPeriod: number = 0, // Optional, defaults to 0
): number => {
  // Validate inputs
  if (!date1 || !date2) return 0;

  // Normalize both dates to midnight UTC to disregard time components
  const normalizedDate1 = normalizeDate(date1);
  const normalizedDate2 = normalizeDate(date2);

  if (!normalizedDate1 || !normalizedDate2) return 0; // Handle invalid dates

  // Add the monthsPeriod to the first date (date1)
  const adjustedDate1 = moment.utc(normalizedDate1).add(monthsPeriod, 'months').toDate();

  // Calculate the difference in days between the adjusted start date and the end date
  const diffInMilliseconds = normalizedDate2.getTime() - adjustedDate1.getTime();
  const diffDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

  return Math.round(diffDays); // Return rounded day difference
};

/**
 * Gets the distribution periods for a given investment class
 * @param investmentClass Investment class info
 * @returns Number of distribution periods
 */
export const getDistributionPeriods = (investmentClass: investmentClassesType): number => {
  return investmentClass.distributionPeriod === 'monthly'
    ? 12
    : investmentClass.distributionPeriod === 'quarterly'
    ? 4
    : investmentClass.distributionPeriod === 'biannual'
    ? 2
    : investmentClass.distributionPeriod === 'yearly'
    ? 1
    : 12;
};
